import React, { useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Upload, X, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InfoHint } from "@/components/InfoHint";
import { logger } from "@/lib/logger";
import { SectionPanel } from "@/views/gooseberry/shared";
import { FIDELITY_LEVELS } from "@/data/gooseBerryData";

const MAX_IMAGE_DIM = 1024;

function resizeImageFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const w0 = img.width, h0 = img.height;
        const scale = Math.min(1, MAX_IMAGE_DIM / Math.max(w0, h0));
        const w = Math.round(w0 * scale);
        const h = Math.round(h0 * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        const mime = file.type && /^image\/(png|jpeg|webp)$/.test(file.type) ? file.type : "image/png";
        const dataUrl = canvas.toDataURL(mime, 0.92);
        resolve({ dataUrl, mime, width: w, height: h });
      };
      img.onerror = () => reject(new Error("Invalid image file"));
      img.src = String(reader.result || "");
    };
    reader.readAsDataURL(file);
  });
}

export function ArtifactUploadPanel({
  imageDataUrl,
  imageMime,
  fidelity,
  setFidelity,
  onImageChange,
  onImageReset,
  language = "en",
}) {
  const t = language === "nl" ? {
    title: "Ontwerp uploaden",
    help: "Upload een afbeelding van je ontwerp: wireframe, schets, mock-up of screenshot. GooseBerry analyseert de visuele layout en combineert die met de context hieronder om feedback of een synthetische dialoog te maken. De afbeelding wordt verkleind tot maximaal 1024px en blijft alleen in deze browsersessie.",
    description: "Upload een wireframe, schets of screenshot van je ontwerp.",
    remove: "Verwijderen",
    fileError: "Upload een afbeeldingsbestand",
    readError: "Afbeelding kon niet gelezen worden",
    drop: "Sleep je ontwerp hierheen",
    fileTypes: "PNG, JPG, WebP of",
    browse: "Bestanden kiezen",
    type: "Type",
    fidelity: "Mate van uitwerking",
    fidelityHelp: "Vertel GooseBerry hoe gedetailleerd je ontwerp is. Lagere uitwerking betekent meer onzekerheid in de feedback.",
  } : {
    title: "Design concept upload",
    help: "Upload an image of your design: a wireframe, sketch, mock-up, or screenshot. GooseBerry analyzes the visual layout and combines it with the context you provide below to generate feedback or a synthetic dialogue. The image is resized to 1024px max and kept only in this browser session.",
    description: "Upload a wireframe, sketch, or screenshot of your design.",
    remove: "Remove",
    fileError: "Please upload an image file",
    readError: "Could not read image",
    drop: "Drag & drop your design here",
    fileTypes: "PNG, JPG, WebP or",
    browse: "Browse files",
    type: "Type",
    fidelity: "Fidelity of the design",
    fidelityHelp: "Tell GooseBerry how detailed your design is. Lower fidelity = expect higher uncertainty in the feedback.",
  };
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t.fileError);
      return;
    }
    try {
      const result = await resizeImageFile(file);
      onImageChange(result);
    } catch (err) {
      logger.error("Image resize failed", err);
      toast.error(t.readError);
    }
  }, [onImageChange]);

  const onDrop = useCallback(async (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) await handleFile(file);
  }, [handleFile, setDragOver]);

  return (
    <SectionPanel
      number="1"
      title={t.title}
      required
      icon={<ImageIcon className="h-4 w-4" />}
      help={
        <>
          {t.help}
        </>
      }
      description={t.description}
      testid="synberry-upload-card"
      actions={imageDataUrl && (
        <Button variant="outline" className="rounded-xl" onClick={() => { onImageReset(); if (fileInputRef.current) fileInputRef.current.value = ""; }} data-testid="artifact-upload-reset-button">
          <X className="h-4 w-4 mr-1" /> {t.remove}
        </Button>
      )}
    >
      {!imageDataUrl ? (
        <div
          className={`mt-1 rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${
            dragOver ? "border-[#61CBA3] bg-[rgba(97,203,163,0.12)]" : "border-black/20 bg-white hover:border-[#61CBA3]/60"
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          data-testid="artifact-upload-dropzone"
        >
          <Upload className="h-6 w-6 mx-auto text-[#61CBA3]" />
          <div className="mt-2 font-medium text-black">{t.drop}</div>
          <div className="text-xs text-black/60">{t.fileTypes}</div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
            data-testid="artifact-upload-file-input"
          />
          <Button
            variant="outline"
            className="mt-2 rounded-xl"
            onClick={() => fileInputRef.current?.click()}
            data-testid="artifact-upload-browse-button"
          >
            {t.browse}
          </Button>
        </div>
      ) : (
        <div className="mt-1">
          <img
            src={imageDataUrl}
            alt="Uploaded design artifact preview"
            className="max-h-[340px] w-auto rounded-xl border border-black/10 mx-auto"
            data-testid="artifact-upload-preview"
          />
          <div className="mt-2 text-xs text-black/60 text-center">{t.type}: {imageMime || "image/*"}</div>
        </div>
      )}

      <div className="mt-4">
        <div className="flex items-center gap-2">
          <Label className="text-xs uppercase tracking-wider text-black/60">{t.fidelity}</Label>
          <InfoHint testid="synberry-fidelity-info">
            {t.fidelityHelp}
          </InfoHint>
        </div>
        <Tabs value={fidelity} onValueChange={setFidelity} data-testid="synberry-fidelity-toggle" className="mt-1">
          <TabsList className="bg-white border border-black/15 rounded-xl p-1">
            {FIDELITY_LEVELS.map((f) => (
              <TabsTrigger
                key={f.id}
                value={f.id}
                className="rounded-lg data-[state=active]:bg-[#61CBA3] data-[state=active]:text-white"
                data-testid={`synberry-fidelity-${f.id}`}
              >
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="mt-1 text-[11px] text-black/55">
          {(FIDELITY_LEVELS.find((f) => f.id === fidelity) || {}).hint}
        </div>
      </div>
    </SectionPanel>
  );
}
